import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Nav } from "../components/Nav.jsx";
import { Footer } from "../sections/Footer.jsx";
import {
  APPLY_ROLES,
  FORM_SUBMIT_AJAX,
  FORM_SUBMIT_CC,
  FORM_SUBMIT_EMAIL,
  NOTIFY_WEBHOOK_URL,
  getWhatsAppHref,
  WHATSAPP_MESSAGES,
} from "../config/forms.js";

async function notifyWebhook(payload) {
  if (!NOTIFY_WEBHOOK_URL) return;
  try {
    await fetch(NOTIFY_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    /* optional */
  }
}

export function ApplyPage() {
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const whatsappHref = getWhatsAppHref(WHATSAPP_MESSAGES.apply);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    const form = e.currentTarget;
    const data = new FormData(form);

    const formFields = {
      fullName: data.get("Full Name"),
      email: data.get("Email"),
      phone: data.get("Phone"),
      role: data.get("Role Applied"),
      portfolio: data.get("Portfolio Link"),
      linkedin: data.get("LinkedIn") || "",
      message: data.get("Message"),
    };

    try {
      const res = await fetch(FORM_SUBMIT_AJAX, {
        method: "POST",
        body: data,
        headers: { Accept: "application/json" },
      });

      if (!res.ok) throw new Error("FormSubmit returned " + res.status);

      await notifyWebhook({
        source: "Alt Tribe — Freelancer Application",
        ...formFields,
        submittedAt: new Date().toISOString(),
      });

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setErrorMsg(
        `There was a problem submitting your application. Please try again, use WhatsApp, or email ${FORM_SUBMIT_EMAIL}.`
      );
    }
  }

  const whatsappAfterSubmit = getWhatsAppHref(
    "Hi Alt Tribe — I just submitted the freelancer application form."
  );

  return (
    <>
      <Nav />
      <main className="apply-page">
        <div className="shell apply-shell">
          <Link to="/" className="apply-back mono">
            ← Back to Alt Tribe
          </Link>

          {status === "success" ? (
            <div className="apply-success reveal in">
              <h1 className="h-mid">Application received.</h1>
              <p className="lead lead-flush">
                Thank you for applying. We&apos;ll review your submission and get back to you by email.
              </p>
              <div className="apply-success-actions">
                <Link to="/" className="btn btn-ghost">
                  Return home
                </Link>
                {whatsappAfterSubmit && (
                  <a
                    href={whatsappAfterSubmit}
                    className="btn btn-lime"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Follow up on WhatsApp
                  </a>
                )}
              </div>
            </div>
          ) : (
            <>
              <header className="apply-header">
                <p className="mono apply-eyebrow">Join the tribe · Talent</p>
                <h1 className="h-large">Freelancer application</h1>
                <p className="lead lead-flush">
                  Prefer a real conversation? Use WhatsApp. Ready to apply formally? Complete the form below.
                </p>
              </header>

              {whatsappHref && (
                <div className="apply-wa-banner">
                  <div>
                    <span className="mono">Questions first?</span>
                    <p>
                      Chat with us on <strong>WhatsApp</strong> — ask about roles, the tribe, or how to join.
                    </p>
                  </div>
                  <a
                    href={whatsappHref}
                    className="btn btn-lime"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open WhatsApp
                  </a>
                </div>
              )}

              <p className="mono apply-form-divider">Or submit the application form</p>

              <form
                className="apply-form"
                onSubmit={handleSubmit}
                encType="multipart/form-data"
                noValidate
              >
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_cc" value={FORM_SUBMIT_CC} />
                <input type="hidden" name="_subject" value="New Freelancer Application — Alt Tribe" />
                <input type="hidden" name="_template" value="table" />
                <input
                  type="hidden"
                  name="_autoresponse"
                  value="Thanks for applying to Alt Tribe. We've received your submission and will be in touch when there's a fit."
                />
                <input type="text" name="_gotcha" className="apply-honeypot" tabIndex={-1} autoComplete="off" />

                <div className="apply-field">
                  <label htmlFor="fullName">Full name</label>
                  <input id="fullName" type="text" name="Full Name" required autoComplete="name" />
                </div>

                <div className="apply-field">
                  <label htmlFor="email">Email address</label>
                  <input id="email" type="email" name="Email" required autoComplete="email" />
                </div>

                <div className="apply-field">
                  <label htmlFor="phone">Phone</label>
                  <input id="phone" type="tel" name="Phone" required autoComplete="tel" />
                </div>

                <div className="apply-field">
                  <label htmlFor="portfolio">Portfolio / website link</label>
                  <input id="portfolio" type="url" name="Portfolio Link" required placeholder="https://" />
                </div>

                <div className="apply-field">
                  <label htmlFor="linkedin">LinkedIn (optional)</label>
                  <input id="linkedin" type="url" name="LinkedIn" placeholder="https://linkedin.com/in/…" />
                </div>

                <div className="apply-field">
                  <label htmlFor="role">Which role are you applying for?</label>
                  <select id="role" name="Role Applied" required defaultValue="">
                    <option value="" disabled>
                      Select role
                    </option>
                    {APPLY_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="apply-field">
                  <label htmlFor="message">Why would you like to work with us?</label>
                  <textarea id="message" name="Message" rows={4} required />
                </div>

                <div className="apply-field">
                  <label htmlFor="attachment">Upload resume / work sample</label>
                  <input
                    id="attachment"
                    type="file"
                    name="attachment"
                    required
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.zip"
                  />
                  <span className="apply-hint mono">PDF, DOC, images, or ZIP — max 10 MB recommended</span>
                </div>

                {status === "error" && (
                  <p className="apply-alert apply-alert--error" role="alert">
                    {errorMsg}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn-primary apply-submit"
                  disabled={status === "sending"}
                >
                  {status === "sending" ? "Submitting…" : "Submit application"}
                  {status !== "sending" && <span className="arr" aria-hidden="true" />}
                </button>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}
